import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarInset,
  SidebarSeparator,
  SidebarTrigger,
} from '../ui/sidebar';
import { BookOpen, ArrowLeft } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { courses, findCourseById, type Course } from '@/data/courses';

interface CourseViewerProps {
  initialCourse?: Course | null;
}

export function CourseViewer({ initialCourse }: CourseViewerProps) {
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(initialCourse || null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleLaunchCourse = (course: Course) => {
    setSelectedCourse(course);
  };

  useEffect(() => {
    if (initialCourse) {
      handleLaunchCourse(initialCourse);
    }
  }, [initialCourse]);

  useEffect(() => {
    const idParam = searchParams.get('courseId');
    if (!idParam) return;

    const course = findCourseById(Number(idParam));
    if (course) {
      handleLaunchCourse(course);
    }
  }, [searchParams]);

  const handleBackToGrid = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    setSelectedCourse(null);
    navigate('/?go=courses', { replace: true });
  };

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="min-h-screen flex w-full">
        {/* Sidebar */}
        <Sidebar side="left" variant="inset" collapsible="icon">
          <SidebarContent className="bg-[#0D1B2A]/95 backdrop-blur-md border-r border-white/20 mt-16">
            <SidebarGroup className="pt-4">
              <SidebarGroupLabel className="text-white/90 px-4 py-2 text-sm">
              Edge AI & IoT Specializations
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="gap-1">
                  {courses.map((course: Course) => (
                    <SidebarMenuItem key={course.course_id}>
                      <SidebarMenuButton
                        isActive={selectedCourse?.course_id === course.course_id}
                        onClick={() => {
                          navigate(`/classroom?courseId=${course.course_id}`);
                          handleLaunchCourse(course);
                        }}
                        className="text-white hover:bg-white/10 data-[active=true]:bg-white/20 gap-3 px-3 py-2.5 min-h-[2.5rem]"
                      >
                        <div
                          className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: course.color }}
                        >
                          <BookOpen size={14} className="text-white" />
                        </div>
                        <span className="text-xs flex-1 leading-tight">{course.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarSeparator className="my-2" />
          </SidebarContent>
        </Sidebar>

        {/* Main content (unchanged except Back button handler) */}
        <SidebarInset className="flex-1">
          {!selectedCourse ? (
            // welcome screen ...
            /* ...existing welcome JSX... */
            <div className="px-4 sm:px-6 lg:px-8">
              {/* Mobile Sidebar Trigger */}
              <div className="md:hidden mb-4">
                <SidebarTrigger className="text-white bg-[#0D1B2A] hover:bg-[#0D1B2A]/90" />
              </div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center py-16"
              >
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-[#00BFFF] to-[#FF6F61] rounded-2xl flex items-center justify-center shadow-2xl">
                  <BookOpen size={48} className="text-white" />
                </div>
                <h1 className="text-5xl md:text-6xl text-white mb-4">
                  Interactive Courses
                </h1>
                <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
                  Select a course from the sidebar to begin your Edge AI & IoT journey.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-12">
                  <Card className="bg-white/95 backdrop-blur-md border-2 border-[#FF6F61]/50">
                    <CardContent className="pt-6 text-center">
                      <div className="text-4xl mb-3">🛰️</div>
                      <h3 className="text-[#0D1B2A] mb-2">Embedded & IoT Foundations</h3>
                      <p className="text-sm text-gray-600">
                        Firmware, sensors, protocols, power, and robust connected devices.
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/95 backdrop-blur-md border-2 border-[#8B5CF6]/50">
                    <CardContent className="pt-6 text-center">
                      <div className="text-4xl mb-3">🤖</div>
                      <h3 className="text-[#0D1B2A] mb-2">Edge AI, Robotics & Perception</h3>
                      <p className="text-sm text-gray-600">
                        Real-time vision, sensor fusion, planning, and autonomous systems.
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/95 backdrop-blur-md border-2 border-[#FFD93D]/50">
                    <CardContent className="pt-6 text-center">
                      <div className="text-4xl mb-3">🚀</div>
                      <h3 className="text-[#0D1B2A] mb-2">Platforms, SoCs & Agentic AI</h3>
                      <p className="text-sm text-gray-600">
                        Heterogeneous accelerators, SoC design, and autonomous AI agents.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            </div>
          ) : (
            // Full Page Simulation / Course View
            <div className="min-h-[calc(100vh-1rem)] flex flex-col px-4 sm:px-6 lg:px-8 py-4">
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                style={{
                  position: 'sticky',
                  top: 'var(--nav-h, 66px)',
                  zIndex: 40,
                  paddingTop: '0.75rem',
                  paddingBottom: '0.75rem',
                }}
              >
                <div
                  className="max-w-6xl mx-auto rounded-2xl px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4 shadow-lg"
                  style={{
                    background:
                      'radial-gradient(circle at top left, rgba(0,191,255,0.22), rgba(5,11,26,0.95))',
                    border: '1px solid rgba(255,255,255,0.10)',
                    backdropFilter: 'blur(14px)',
                    WebkitBackdropFilter: 'blur(14px)',
                  }}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <SidebarTrigger className="md:hidden" />
                      <Button
                        onClick={handleBackToGrid}
                        variant="ghost"
                        size="sm"
                        className="flex-shrink-0 rounded-full border border-white/20 bg-white/5 text-white/90 hover:bg-white/10 hover:border-white/40 px-3 py-1.5"
                      >
                        <ArrowLeft className="mr-2" size={16} />
                        Back
                      </Button>
                    </div>
                    {selectedCourse && (
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0 shadow-md"
                        style={{ backgroundColor: selectedCourse.color }}
                      >
                        <BookOpen size={20} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h2 className="text-base sm:text-lg text-white truncate">
                          {selectedCourse.title}
                        </h2>
                        <span className="hidden sm:inline-block text-[11px] uppercase tracking-wide text-[#00BFFF]">
                          EdgeAI Course
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-white/70 truncate">
                        {selectedCourse.description}
                      </p>
                    </div>
                  </div>
                  {selectedCourse && (
                    <span className="hidden sm:inline-block text-xs px-3 py-1 rounded-full bg-white/10 text-white/80">
                      Course ID: {selectedCourse.course_id}
                    </span>
                  )}
                </div>
              </motion.div>

              {/* Simulation Iframe */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex-1 flex justify-center"
              >
                <div className="w-full max-w-6xl mx-auto mt-4 mb-6">
                  <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-black/5">
                    <iframe
                      src={selectedCourse.url}
                      className="w-full"
                      style={{
                        minHeight: 'calc(100vh - var(--nav-h, 104px) - 160px)',
                      }}
                      title={selectedCourse.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
